
import {
  Baby,
  Utensils,
  MessageCircle,
  Smile,
  Hand,
  Meh,
  UserCog,
  Zap,
  Wind,
  Bird,
  BookOpen,
  Smartphone,
  MessageSquare,
  MessagesSquare,
  Angry,
  GraduationCap,
  Mic2,
  BarChart,
  Rocket,
  Building,
  Briefcase,
  Crown,
  User,
  Brain,
  LucideIcon,
  CircleDollarSign,
  Flower,
  Boxes,
  Megaphone,
  SquareSplitHorizontal
} from "lucide-react";
import React from "react";

export const rankDescriptions = [
  "Just learning the ropes! Your words may be wobbly, but you're making noise in the Babblegram world.",
  "Talking with your mouth full—literally! Chomping through words and snacks with equal enthusiasm.",
  "You're stringing words together like a pro—watch out world, the chatter is real!",
  "Every sentence is a new adventure. You've got plenty to say, and you're just getting started.",
  "Asking \"why?\" about everything. You won't stop until you've mastered the art of the babble.",
  "The words are there… somewhere! But hey, progress is progress, right?",
  "If there's a word to be found, you'll find it—and share it with everyone!",
  "Talking circles around your competition, even if you're not quite sure where you're going.",
  "No thought left unspoken. You're filling every silence with a steady stream of chatter.",
  "Bright, bubbly, and buzzing with words—your streak is soaring!",
  "You've found your voice and it's loud, proud, and full of wordplay.",
  "More words, more wisdom—your vocabulary is hitting its growth spurt!",
  "Quick, clever, and always with something to say. Your babble is gaining finesse.",
  "No text left unread, no word left unsaid. You're fluent in the language of Babblegram.",
  "Sharp, savvy, and full of fast-talking flair. Your word game is getting serious.",
  "A mile-a-minute chatterbox! Your words are coming faster than ever.",
  "Full of attitude and opinions—your words might roll their eyes, but they still make an impact!",
  "Occasionally tripping over words, but always bouncing back. Learning is loud!",
  "You've earned your babbling degree—congrats on making the grade!",
  "You talk the talk and walk the walk. Professional level wordsmith unlocked!",
  "You're paving the way with powerful words and unstoppable chatter.",
  "Words are your superpower, and you're running this babble-filled business.",
  "Confident, commanding, and captivating. Your streak is leading the conversation!",
  "You've climbed the ranks, and now everyone hangs on your every word.",
  "Full of wisdom and words—your stories are legendary.",
  "No rush, no filter, just endless conversation. Every word counts!",
  "Dropping knowledge with every syllable. Your words guide the next generation of babblers.",
  "A master storyteller with endless charm. Every chat with you is a lesson in wordplay.",
  "Megaphone in hand, leading the charge with words that demand attention!",
  "The ultimate word wizard. You've conquered the game—now you reign supreme!"
];

export const getStreakRank = (streak: number): string => {
  const ranks = [
    'Baby Babbler',
    'Gabby Grubber',
    'Talkative Toddler',
    'Chatty Child',
    'Nagging Nipper',
    'Tongue-Tied Tyke',
    'Gossiping Goober',
    'Rambling Rascal',
    'Yabbering Youngster',
    'Chirpy Child',
    'Conversing Cub',
    'Prattling Preteen',
    'Twittering Tween',
    'Talkative Teen',
    'Articulate Adolescent',
    'Jabbering Juvenile',
    'Mouthy Minor',
    'Sputtering Student',
    'Gabbing Graduate',
    'Chattering Careerist',
    'Talkative Trailblazer',
    'Babbling Boss',
    'Loquacious Leader',
    'Blabbering Bigwig',
    'Gabbing Grandparent',
    'Rambling Retiree',
    'Musing Mentor',
    'Gregarious Grandpa',
    'Articulate Elder',
    'Babblegram Grandmaster'
  ];

  if (streak === 0 || streak > ranks.length) return '';
  
  return ranks[streak - 1];
};

export const getRankDescription = (streak: number): string => {
  if (streak === 0) return "Just starting out! Keep playing to build your streak.";
  if (streak > 0 && streak <= rankDescriptions.length) {
    return rankDescriptions[streak - 1];
  }
  return `You've reached a ${streak}-day streak!`;
};

const createCustomMessageCircleHeart = () => {
  return function CustomMessageCircleHeart(props: React.SVGProps<SVGSVGElement>) {
    return React.createElement(
      'div',
      { style: { position: 'relative', display: 'inline-block' } },
      [
        React.createElement(MessageCircle, { ...props, key: 'circle' }),
        React.createElement(
          'span',
          {
            key: 'heart',
            style: {
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -45%)',
              fontSize: '60%',
              color: props.color || 'currentColor'
            }
          },
          '❤️'
        )
      ]
    );
  };
};

export const getRankIcon = (rankIndex: number): LucideIcon | null => {
  const rankIcons: LucideIcon[] = [
    Baby,               // 1. Baby Babbler
    Utensils,           // 2. Gabby Grubber
    MessageCircle,      // 3. Talkative Toddler
    Smile,              // 4. Chatty Child
    Hand,               // 5. Nagging Nipper
    Meh,                // 6. Tongue-Tied Tyke
    UserCog,            // 7. Gossiping Goober
    Zap,                // 8. Rambling Rascal
    Wind,               // 9. Yabbering Youngster
    Bird,               // 10. Chirpy Child
    MessagesSquare,     // 11. Conversing Cub (Using MessagesSquare - plural)
    BookOpen,           // 12. Prattling Preteen
    Smartphone,         // 13. Twittering Tween
    MessageSquare,      // 14. Talkative Teen
    MessageSquare,      // 15. Articulate Adolescent (Using MessageSquare)
    MessageCircle,      // 16. Jabbering Juvenile (Will be replaced with custom heart icon below)
    Angry,              // 17. Mouthy Minor
    Boxes,              // 18. Sputtering Student (Updated to Boxes)
    GraduationCap,      // 19. Gabbing Graduate (Updated to GraduationCap)
    BarChart,           // 20. Chattering Careerist
    Rocket,             // 21. Talkative Trailblazer
    Building,           // 22. Babbling Boss
    Briefcase,          // 23. Loquacious Leader (replaced UserTie with Briefcase)
    CircleDollarSign,   // 24. Blabbering Bigwig (Updated to CircleDollarSign)
    User,               // 25. Gabbing Grandparent
    Flower,             // 26. Rambling Retiree (Updated to Flower)
    Brain,              // 27. Musing Mentor
    Megaphone,          // 28. Gregarious Grandpa (Updated to Megaphone)
    SquareSplitHorizontal, // 29. Articulate Elder (Updated to SquareSplitHorizontal)
    Crown               // 30. Babblegram Grandmaster
  ];
  
  if (rankIndex <= 0 || rankIndex > rankIcons.length) {
    return null;
  }
  
  if (rankIndex === 16) {
    const CustomMessageCircleHeart = createCustomMessageCircleHeart();
    return CustomMessageCircleHeart as unknown as LucideIcon;
  }
  
  return rankIcons[rankIndex - 1];
};
